import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import { Modal } from 'components/ui';
import { b } from 'utils/i18nUtils';
import { validatePlateNumber } from 'utils/formUtils';
import { useDispatch, useSelector } from 'hooks';
import { setLoginData } from 'modules/auth/actions';
import { login } from 'modules/auth/thunk';
import {
  getLoginData,
  hasRegistrationError as hasRegistrationErrorSelector,
  hasLocationIdError as hasLocationIdErrorSelector,
} from 'modules/auth/selectors';
import { isLoadingLogin } from 'modules/selfServices/selectors';
import { getConnectionTypes } from 'modules/dealers/selectors';

import LicensePlateInput from '../../common/LicensePlateInput';
import KioskModalActions from './KioskModalActions';
import { SelfOfficeLoginMethod } from 'modules/dealers/types/ContextResponse';
import { LoginModals as LicensePlateModalProps } from './types';
import KioskLocationIdInput from 'components/Login/KioskLocationIdInput';


const LicensePlateModal: React.FC<LicensePlateModalProps> = ({ onClose, onSuccess, onError }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { state: pageState } = useLocation();
  const isAtDealership = pageState?.atDealership;

  const loginData = useSelector(getLoginData);
  const isLoading = useSelector(isLoadingLogin);
  const connectionTypes = useSelector(getConnectionTypes);
  const hasRegistrationError = useSelector(hasRegistrationErrorSelector);
  const hasLocationIdError = useSelector(hasLocationIdErrorSelector);

  const { registration = '', locationId } = loginData;

  const [plateNumberError, setPlateNumberError] = React.useState<string>();

  const licensePlateError = plateNumberError || hasRegistrationError;
  const locationIdValidation = !isAtDealership || Boolean(locationId);
  const canSubmit = registration.length > 0 && !plateNumberError && locationIdValidation && !isLoading;

  const handlePlateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const upper = value.toUpperCase();
    dispatch(setLoginData({ ...loginData, registration: upper }));
    setPlateNumberError(validatePlateNumber(upper, false, intl));
  };

  const handleLocationIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    dispatch(setLoginData({ ...loginData, locationId: value ? Number(value) : undefined }));
  };

  const handleSubmit = async () => {
    try {
      await dispatch(login({
        registration,
        connectionTypes,
        qrCode: undefined,
        type: pageState?.type,
        searchType: SelfOfficeLoginMethod.REGISTRATION,
        locationId: isAtDealership ? locationId : undefined,
      })).unwrap();
      onSuccess(SelfOfficeLoginMethod.REGISTRATION);
    } catch (error) {
      onError(error);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      className="max-w-modal"
      title={
        <div role="heading" aria-level={2} data-testid="license-plate-modal-title" className="text-icon font-bold">
          <FormattedMessage id="login.licensePlateModal.title" defaultMessage="License plate" />
        </div>
      }
    >
      <div className="flex flex-col gap-9">
        <div className="text-center text-s">
          <FormattedMessage
            id="login.licensePlateModal.label"
            defaultMessage="Please enter the <b>license plate of your vehicle</b> in order to access your appointment"
            values={{ b }}
            tagName="span"
          />
        </div>
        <LicensePlateInput
          value={registration}
          error={licensePlateError}
          onChange={handlePlateChange}
          onSubmit={handleSubmit}
          canSubmit={canSubmit}
          center
          labelClassName="text-center"
          data-testid="plate-input"
        />
        {isAtDealership && (
          <KioskLocationIdInput
            value={locationId}
            canSubmit={canSubmit}
            onSubmit={handleSubmit}
            error={hasLocationIdError}
            onChange={handleLocationIdChange}
          />
        )}
        <KioskModalActions
          onClose={onClose}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          disabled={!canSubmit}
        />
      </div>
    </Modal>
  );
};

export default LicensePlateModal;
